
import { useLaneOptions } from '../../../hooks/useLaneOptions';

interface FilterSidebarProps {
  filters: {
    lane: string;
    city: string;
    verified: boolean;
    hasNewRelease: boolean;
    hasMemberships: boolean;
    roles: string;
  };
  onFilterChange: (key: string, value: string | boolean) => void;
  onClearFilters: () => void;
  lanes?: string[];
}

const cities = [
  'Nairobi',
  'Mombasa',
  'Kisumu',
  'Eldoret',
  'Nakuru',
  'Thika'
];

const roles = [
  'MC',
  'Producer',
  'DJ',
  'Collective'
];

export default function FilterSidebar({
  filters,
  onFilterChange,
  onClearFilters,
  lanes,
}: FilterSidebarProps) {
  const { lanes: laneOptions } = useLaneOptions({
    initialLanes: lanes,
    skipFetch: Boolean(lanes && lanes.length > 0),
  });
  const effectiveLanes = lanes && lanes.length > 0 ? lanes : laneOptions;
  const hasActiveFilters = Object.values(filters).some(value => 
    value !== '' && value !== false
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Lane/Genre */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Lane/Genre</h4>
          <div className="space-y-2">
            {effectiveLanes.map((lane) => (
              <label key={lane} className="flex items-center">
                <input
                  type="radio"
                  name="lane"
                  value={lane}
                  checked={filters.lane === lane}
                  onChange={(e) => onFilterChange('lane', e.target.checked ? lane : '')}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{lane}</span>
              </label>
            ))}
          </div>
        </div>

        {/* City/Scene */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">City/Scene</h4>
          <div className="space-y-2">
            {cities.map((city) => (
              <label key={city} className="flex items-center">
                <input
                  type="radio"
                  name="city"
                  value={city}
                  checked={filters.city === city}
                  onChange={(e) => onFilterChange('city', e.target.checked ? city : '')}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{city}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Status</h4>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.verified}
                onChange={(e) => onFilterChange('verified', e.target.checked)}
                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Verified only</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasNewRelease}
                onChange={(e) => onFilterChange('hasNewRelease', e.target.checked)}
                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Has new release</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasMemberships}
                onChange={(e) => onFilterChange('hasMemberships', e.target.checked)}
                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Has memberships</span>
            </label>
          </div>
        </div>

        {/* Roles */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Roles</h4>
          <div className="space-y-2">
            {roles.map((role) => (
              <label key={role} className="flex items-center">
                <input
                  type="radio"
                  name="roles"
                  value={role}
                  checked={filters.roles === role}
                  onChange={(e) => onFilterChange('roles', e.target.checked ? role : '')}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{role}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
